import type { LeadRow } from '@/domains/sales/services/salesService';

/**
 * Agent introduction email — single source of truth for the copy/mailto
 * actions on the lead page + leads list. Wording mirrors
 * _bmad-output/SALES_DOCS/AGENT_INTRO_EMAIL_TEMPLATES.md (Templates A + B).
 *
 * Only lead-specific fields are merged (first name, company). Agent-specific
 * details stay as a [Your name] stub — the agent fills it / their Outlook
 * signature appends it. Sent from the agent's OWN mailbox, deliberately NOT
 * through the sighthost.online transactional pipeline (protects its reputation).
 */

export type IntroVariant = 'warm' | 'cold';

export function buildIntroEmail(
  lead: LeadRow,
  variant: IntroVariant = 'warm',
): { subject: string; body: string } {
  const first = (lead.full_name ?? '').trim().split(/\s+/)[0] || 'there';
  const company = lead.company?.trim() || 'your business';

  if (variant === 'cold') {
    return {
      subject: `Affordable local hosting for ${company}`,
      body: [
        `Hi ${first},`,
        ``,
        `I'm reaching out from SightHost, a South African web hosting and domain company. We help small businesses like ${company} get online affordably — with local support when you actually need it.`,
        ``,
        `A few things our customers like:`,
        ` - cPanel hosting from R79/month, no contract`,
        ` - A free .co.za domain with every plan`,
        ` - Business email + free SSL security`,
        ` - POPIA-compliant, hosted in South Africa`,
        ``,
        `If getting online — or moving off a slow/expensive host — is on your list, I'd be glad to point you to the right plan. Worth a quick chat, or a reply with what you're after?`,
        ``,
        `Best regards,`,
        `[Your name] · SightHost`,
        `www.sighthost.online`,
      ].join('\n'),
    };
  }

  return {
    subject: `${first} — I'll be your SightHost contact`,
    body: [
      `Hi ${first},`,
      ``,
      `I'll be your point of contact at SightHost for getting ${company} online.`,
      ``,
      `SightHost is a South African web hosting and domain provider built for small businesses — fast, reliable cPanel hosting, a free .co.za domain, business email, and real local support (no call-centre runaround). Plans start at R79/month with no long-term contract.`,
      ``,
      `Happy to help you pick the right plan, or move an existing site across. Do you have 10 minutes for a quick call this week? Or just reply here and I'll answer any questions.`,
      ``,
      `Looking forward to helping ${company} get online.`,
      ``,
      `Best regards,`,
      `[Your name] · SightHost`,
      `www.sighthost.online`,
    ].join('\n'),
  };
}

/** Full block for clipboard: To / Subject / body. */
export function introClipboardText(lead: LeadRow, variant: IntroVariant): string {
  const { subject, body } = buildIntroEmail(lead, variant);
  return `${lead.email ? `To: ${lead.email}\n` : ''}Subject: ${subject}\n\n${body}`;
}

/** mailto: link that opens the agent's default mail client (Outlook) pre-filled. */
export function introMailtoHref(lead: LeadRow, variant: IntroVariant): string {
  const { subject, body } = buildIntroEmail(lead, variant);
  return `mailto:${lead.email ?? ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
